import { useEffect, useState } from 'react'

import { formatPhone, formatDate } from 'admin/adminutil'

import { generateAllUserPaidStatus, handleSort, performSort, sortSpan } from '../auctionsadminutil'

import { formatName } from '../auctionsadminutil'

import css from '../UserDetail/sass/liveauction.module.scss'
import adminCss from '../sass/adminpage.module.scss'
import themeCss from '../../components/sass/theme.module.scss'
import AuctionRegistrationRow from '../UserDetail/AuctionRegistrationRow'
import ClosedManageCheckout from './ClosedManageCheckout'
import ConfirmSendPayReminders from '../ConfirmModal/ConfirmSendPayReminders'
import SortNotice from '../SortNotice'
import { canEditAuction, isAuctionOwner } from '../permissionsutil'
import StatSummary from '../StatSummary'
import StatPaidSummary from '../StatPaidSummary'

const ADMIN_MARKED_PAID_NOTE = 'some marked paid by admin'

const parseTransactionPaymentDetails = (paymentDetails) => {
  if (!paymentDetails) return null

  try {
    return typeof paymentDetails === 'string' ? JSON.parse(paymentDetails) : paymentDetails
  } catch (error) {
    console.log('Unable to parse payment details', { paymentDetails, error })
    return null
  }
}

const addTransactionEntry = (transactions, paymentType, transactionId) => {
  if (!paymentType || !transactionId) return
  if (!transactions[paymentType]) transactions[paymentType] = []
  if (!transactions[paymentType].includes(transactionId)) transactions[paymentType].push(transactionId)
}

const formatGroupedTransactions = (transactions) => {
  const formattedTransactions = {}

  Object.entries(transactions).forEach(([paymentType, transactionIds]) => {
    if (paymentType === 'adminMarkedPaid') {
      if (transactionIds) formattedTransactions[paymentType] = ADMIN_MARKED_PAID_NOTE
      return
    }

    if (!transactionIds?.length) return
    formattedTransactions[paymentType] = transactionIds.join(', ')
  })

  return formattedTransactions
}

const getMatchingItemsPaid = (transactionData, matchConfig) => {
  const itemsPaid = transactionData?.itemsPaid || []

  return itemsPaid.filter((paidItem) => {
    if (paidItem?.type !== matchConfig.type) return false
    if (matchConfig.type === 'donation') return true
    return paidItem?.id === matchConfig.id
  })
}

const getMatchedAmount = (transactionData, matchConfig) => {
  const matchingItemsPaid = getMatchingItemsPaid(transactionData, matchConfig)
  if (matchingItemsPaid.length === 0) return null
  return matchingItemsPaid.reduce((sum, paidItem) => sum + (Number(paidItem.amountPaid) || 0), 0)
}

const addTransactionTrace = (traceEntries, { userName, paymentType, transactionId, amount, sourceType, sourceLabel }) => {
  traceEntries.push({ userName, paymentType, transactionId, amount, sourceType, sourceLabel })
}

const collectPaymentTransactions = ({ transactions, traceEntries, paymentDetails, sourceType, sourceLabel,
  userName, adminMarkedAmount, matchConfig }) => {
  if (!paymentDetails) return

  if (typeof paymentDetails.message === 'string' && paymentDetails.message.toLowerCase().includes('marked as paid by admin')) {
    transactions.adminMarkedPaid = true

    addTransactionTrace(traceEntries, {
      userName,
      paymentType: 'adminMarkedPaid',
      transactionId: paymentDetails.message,
      amount: adminMarkedAmount,
      sourceType,
      sourceLabel,
    })

    return
  }

  const giftTransactionId = paymentDetails.ccLambdaResponses?.gift?.success
    ? paymentDetails.ccLambdaResponses.gift.ccLambdaResponse?.transactionNumber
    : null
  const giftAmount = getMatchedAmount(paymentDetails.ccLambdaResponses?.gift, matchConfig)

  if (giftTransactionId && giftAmount !== null) {
    addTransactionEntry(transactions, 'gift', giftTransactionId)

    addTransactionTrace(traceEntries, {
      userName,
      paymentType: 'gift',
      transactionId: giftTransactionId,
      amount: giftAmount,
      sourceType,
      sourceLabel,
    })
  }

  const nonGiftTransactionId = paymentDetails.ccLambdaResponses?.nonGift?.success
    ? paymentDetails.ccLambdaResponses.nonGift.ccLambdaResponse?.transactionNumber
    : null
  const nonGiftAmount = getMatchedAmount(paymentDetails.ccLambdaResponses?.nonGift, matchConfig)

  if (nonGiftTransactionId && nonGiftAmount !== null) {
    addTransactionEntry(transactions, 'nonGift', nonGiftTransactionId)

    addTransactionTrace(traceEntries, {
      userName,
      paymentType: 'nonGift',
      transactionId: nonGiftTransactionId,
      amount: nonGiftAmount,
      sourceType,
      sourceLabel,
    })
  }
}

const getClosedAttendeeTransactions = (auctionDetail, userId, userName) => {
  const transactions = {
    gift: [],
    nonGift: [],
    adminMarkedPaid: false,
  }
  const traceEntries = []

  ;(auctionDetail?.Items || []).forEach((item) => {
    if (item.CurrentHighBidder !== userId || !item.PaymentDetails) return

    collectPaymentTransactions({
      transactions,
      traceEntries,
      paymentDetails: parseTransactionPaymentDetails(item.PaymentDetails),
      sourceType: 'Item',
      sourceLabel: item.Title || item.ItemCode || item.ItemId,
      userName,
      adminMarkedAmount: item.CurrentBid,
      matchConfig: {
        type: 'bid',
        id: item.ItemId,
      },
    })
  })

  ;(auctionDetail?.FixedItems || []).forEach((fixedItem) => {
    ;(fixedItem.Payments || []).forEach((payment) => {
      if (payment.UserId !== userId || !payment.PaymentDetails) return

      collectPaymentTransactions({
        transactions,
        traceEntries,
        paymentDetails: parseTransactionPaymentDetails(payment.PaymentDetails),
        sourceType: 'Fixed Item',
        sourceLabel: fixedItem.Title || fixedItem.ItemCode || fixedItem.FixedItemId,
        userName,
        adminMarkedAmount: (payment.Quantity || 0) * (fixedItem.PurchasePrice || 0),
        matchConfig: {
          type: 'fixed',
          id: fixedItem.FixedItemId,
        },
      })
    })
  })

  ;(auctionDetail?.Registrations || []).forEach((registration) => {
    if (registration?.User?.UserId !== userId || !registration.DonationPayment) return

    collectPaymentTransactions({
      transactions,
      traceEntries,
      paymentDetails: parseTransactionPaymentDetails(registration.DonationPayment),
      sourceType: 'Donation',
      sourceLabel: 'Donation',
      userName,
      adminMarkedAmount: registration.DonationTotal,
      matchConfig: {
        type: 'donation',
      },
    })
  })

  return {
    transactions: formatGroupedTransactions(transactions),
    traceEntries,
  }
}

export default function ClosedAttendeeView(props) {
  const [showCheckInFilter, setShowCheckInFilter] = useState(true)
  const [showNotCheckedInFilter, setShowNotCheckedInFilter] = useState(true)
  const [nameFilter, setNameFilter] = useState('')
  const [onlyWithBalanceDue, setOnlyWithBalanceDue] = useState(false)

  const [managedAttendee, setManagedAttendee] = useState()
  const [checkoutView, setCheckoutView] = useState(false)

  //array of truth for all registrations in auction
  const [allRegistrations, setAllRegistrations] = useState([])

  //filtered list based off selection
  const [displayedRegistrations, setDisplayedRegistrations] = useState([])
  const [modalData, setModalData] = useState(undefined)
  const [showModal, setShowModal] = useState(false)

    //for table sorting
    const [sortOptions, setSortOptions] = useState({ key: '', dir: 'asc' })

    let tableFormatter = [
      ['FullName', 'Attendee Name'],
      ['TotalDue', 'Total Amount Due'],
      ['TotalPaid', 'Total Amount Paid'],
      ['ItemsWon', 'Items Won'],
      ['CheckStr', 'Checked In?'],
      ['EmailStr', 'Email'],
      ['CrmId', 'CRM ID'],
      ['PhoneStr', 'Phone'],
      ['RegDate', 'Register Date'],
      ['Transactions', 'Transaction IDs'],
    ]

  const auctionDetail = props.auction

  if (!auctionDetail.NeedsCheckin) tableFormatter.splice(4, 1)

  //remove email, crm id and phone for collaborators
  if (!isAuctionOwner(auctionDetail)) {
    tableFormatter = [
      ['FullName', 'Attendee Name'],
      ['TotalDue', 'Total Amount Due'],
      ['TotalPaid', 'Total Amount Paid'],
      ['ItemsWon', 'Items Won'],
      ['CheckStr', 'Checked In?'],
      ['RegDate', 'Register Date'],
      ['Transactions', 'Transaction IDs'],
    ]

    if (!auctionDetail.NeedsCheckin) tableFormatter.splice(4, 1)
  }
  

  useEffect(() => {
    if (auctionDetail) {
  
      const itemObj = generateAllUserPaidStatus(auctionDetail)
      const transactionTraceSnapshot = {}


      const formattedRegistrations = auctionDetail.Registrations.map((reg) => {
        const userData = itemObj[reg.User.UserId]
        const fullName = formatName(reg)
        const transactionData = getClosedAttendeeTransactions(auctionDetail, reg.User.UserId, fullName)

        transactionTraceSnapshot[reg.User.UserId] = {
          userName: fullName,
          transactions: transactionData.transactions,
          traceEntries: transactionData.traceEntries,
        }

        return {
          ...reg,
          FullName: fullName,
          TotalDue: userData?.totalDue || '0',
          TotalPaid: userData?.totalPaid || '0',
          ItemsWon: userData?.itemsWon || '0',
          Transactions: transactionData.transactions,
          CheckStr: (reg.IsCheckedIn ? 'Yes' : "No"),
          EmailStr: reg.User.ContactEmail,
          CrmId :reg.User.CrmId,
          PhoneStr :formatPhone(reg.User.ContactPhone),
          RegDate : formatDate(reg.DateCreated, true),
        }
      }) 

      //console log to check transaction status by user id
      // console.log('[ClosedAttendeeView] transaction trace snapshot', transactionTraceSnapshot)

      setAllRegistrations(formattedRegistrations)
    }
  }, [auctionDetail])

  //use effect to see if any attendees still owe - to show the send payment remind button
  useEffect(() => {

    let auctionHasUnpaidAttendees = false

    displayedRegistrations.forEach((reg) => {
      let parsed = parseInt(reg.TotalDue)
      if (typeof(parsed) === 'number' && parsed > 0) auctionHasUnpaidAttendees = true
    })

    if (auctionHasUnpaidAttendees) setModalData({auctionId: auctionDetail.AuctionId} )
    else setModalData(undefined)

  }, [displayedRegistrations])

  //filter for displayed attendees
  useEffect(() => {

    let filtered = [...allRegistrations]
    
    if (filtered.length === 0) return

    if (nameFilter) {
      const normalizedNameFilter = nameFilter.toLowerCase()

      filtered = filtered.filter((registration) => {
        return (registration.User.FirstName?.toLowerCase().includes(normalizedNameFilter)) ||
          (registration.User.LastName?.toLowerCase().includes(normalizedNameFilter))
      })
    }

    if (onlyWithBalanceDue) {
      filtered = filtered.filter((registration) => Number(registration.TotalDue) > 0)
    }
    
    const output = filtered.filter((registration) => {
      return (showCheckInFilter && registration.IsCheckedIn) || (showNotCheckedInFilter && !registration.IsCheckedIn)
    })

    let sorted = []

    if (sortOptions.key === '') sorted = performSort(output, {key: 'FullName', direction: 'asc'})
    else sorted = performSort(output, sortOptions)

    setDisplayedRegistrations(sorted)

  }, [showNotCheckedInFilter, showCheckInFilter, allRegistrations, nameFilter, onlyWithBalanceDue, sortOptions])

  const sendReminderEmails = async () => {
    setShowModal(true)
  }

  const resetFilters = () => {
    setShowCheckInFilter(true)
    setShowNotCheckedInFilter(true)
    setNameFilter('')
    setOnlyWithBalanceDue(false)
  }

  const formatTransactionsForCopy = (transactions) => {
    if (!transactions || Object.keys(transactions).length === 0) return ''

    return Object.entries(transactions)
      .map(([transactionLabel, transactionId]) => transactionLabel === 'adminMarkedPaid' ? transactionId : `${transactionLabel}: ${transactionId}`)
      .join(', ')
  }

  // make sure to update this if the columns in LiveAuctionRegistrationRow change
  const copyAttendeesToClipboard = () => {

    let textToCopy = ''

    //auction owners see all data
    if (isAuctionOwner(auctionDetail)) {
      if (auctionDetail?.NeedsCheckin) {
        textToCopy = [tableFormatter.map(headerName => headerName[1]).join('\t')]
        displayedRegistrations.forEach(row => {
          textToCopy.push([
            formatName(row), "$"+row.TotalDue, "$"+row.TotalPaid, row.ItemsWon, row.IsCheckedIn ? 'Yes' : "No",
            row.User.ContactEmail, row.User.CrmId.length > 0 ? '="'+row.User.CrmId+'"' : '',
            formatPhone(row.User.ContactPhone),formatDate(row.DateCreated), formatTransactionsForCopy(row.Transactions),
          ].join('\t'))
        })
      } else {
        textToCopy = [tableFormatter.map(headerName => headerName[1]).join('\t')]
        displayedRegistrations.forEach(row => {
          textToCopy.push([
            formatName(row), "$"+row.TotalDue, "$"+row.TotalPaid, row.ItemsWon, row.User.ContactEmail,
            row.User.CrmId.length > 0 ? '="'+row.User.CrmId+'"' : '', formatPhone(row.User.ContactPhone),
            formatDate(row.DateCreated), formatTransactionsForCopy(row.Transactions),
          ].join('\t'))
        })
      }

      // for collaborators, hide email, CRM ID, and phone
    } else {
      if (auctionDetail?.NeedsCheckin) {
        textToCopy = [tableFormatter.map(headerName => headerName[1]).join('\t')]
        displayedRegistrations.forEach(row => {
          textToCopy.push([
            formatName(row), "$"+row.TotalDue, "$"+row.TotalPaid, row.ItemsWon, row.IsCheckedIn ? 'Yes' : "No",
            formatDate(row.DateCreated), formatTransactionsForCopy(row.Transactions),
          ].join('\t'))
        })
      } else {
        textToCopy = [tableFormatter.map(headerName => headerName[1]).join('\t')]
        displayedRegistrations.forEach(row => {
          textToCopy.push([
            formatName(row), "$"+row.TotalDue, "$"+row.TotalPaid, row.ItemsWon,
            formatDate(row.DateCreated), formatTransactionsForCopy(row.Transactions),
          ].join('\t'))
        })
      }
    }

    navigator.clipboard.writeText(textToCopy.join('\n'))
  }

  const toAttendeePage = (attendeeFromRow) => {
    setManagedAttendee(attendeeFromRow)
    setCheckoutView(true)

  }

  const toAllAttendeePage = () => {
    setCheckoutView(false)
  }

  if (!auctionDetail) return (<></>)

  if (checkoutView) {
    return <ClosedManageCheckout auctionCode={auctionDetail.AuctionCode} managedAttendee={managedAttendee} toAllAttendeePage={toAllAttendeePage} />
  }

  return (
    <div className={css['live-auction-container']}>
      
      <div className={adminCss['admin-auction-items-title']}> 
        <h2 className={adminCss['admin-auction-page-tab-header']}>
          Registered Attendees
        </h2>

        <StatPaidSummary auction={props.auction} />
        <StatSummary auction={props.auction} />
      </div>
      
      {auctionDetail.NeedsCheckin && <button className={`${themeCss['button']} ${themeCss['FilledBlue']}`}>
        Go to Check-in App
      </button>}

       {modalData && canEditAuction(auctionDetail) &&
                <button onClick={() => sendReminderEmails()} className={`${themeCss['button']} ${themeCss['FilledWhite']}`}>
                  Send Payment Reminder Email To All Guests With Balance Due
                </button>}

      {showModal && <ConfirmSendPayReminders setShowConfirmModal={setShowModal} data={modalData} /> }
      
      <div className={css['live-auction-attendee-filters']}>
        <div className={css['live-auction-filter']}>
          <label>
            Attendee Name
          </label>
          <input type='text' value={nameFilter} onChange={(e) => setNameFilter(e.target.value)}/>
        </div>

        <div className={css['live-auction-filter']}>
          <div className={css['live-auction-filter-checkbox']}>
            <input
              type='checkbox'
              checked={onlyWithBalanceDue}
              onChange={() => setOnlyWithBalanceDue(!onlyWithBalanceDue)}
            />
            <label>Only with Balance Due</label>
          </div>
        </div>

        {auctionDetail.NeedsCheckin && <div className={css['live-auction-filter']}>
          <div className={css['live-auction-filter-checkbox']}>
            <input type='checkbox' checked={showCheckInFilter} onChange={(e) => setShowCheckInFilter(!showCheckInFilter)} />
            <label> Show Checked in Users</label>
          </div>
          <div className={css['live-auction-filter-checkbox']}>
            <input type='checkbox' checked={showNotCheckedInFilter} onChange={(e) => setShowNotCheckedInFilter(!showNotCheckedInFilter)} />
            <label> Show Users Not Checked In</label>
          </div>
        </div>}

        <button className={`${themeCss['button']} ${themeCss['FilledYellow']}`} onClick={resetFilters}>
          Reset Filters
        </button>
      </div>

      {isAuctionOwner(auctionDetail) && <div>
        Click on the Manage Checkout under "Total Amount Due" to go to the checkout page for that attendee.
      </div>}

      <div>
        <SortNotice />
        <table className={themeCss['data-table']}>
          <thead>
            <tr>
            {tableFormatter.map((ele) => {
              return (
                <th onClick={() => handleSort(ele[0], sortOptions, setSortOptions)} key={ele[1]}>
                  {ele[1]} {sortSpan(ele[0], sortOptions)}</th>
                )})}
            </tr>
          </thead>
          <tbody>
            {displayedRegistrations && displayedRegistrations?.map((registration) => {
            return <AuctionRegistrationRow key={registration.RegistrationId} registration={registration} 
              auctionDetail={auctionDetail} toAttendeePage={toAttendeePage}
              />
            })}
          </tbody>
        </table>
      </div>
      
      <button className={`${themeCss['button']} ${themeCss['FilledWhite']} ${themeCss['copyclipboard-button']}`} onClick={copyAttendeesToClipboard}>
        Copy Data to Clipboard
      </button>

    </div>
  )
}
